import { useCallback, useMemo } from "react";
import { SMSLogEntry, useTwilioSmsLogsQuery } from "./use-twilio-sms-logs-query";
import { CallLogEntry, useTwilioCallLogsQuery } from "./use-twilio-call-logs-query";

export type PhoneLogType = "SMS" | "Voice";

/** A unified phone-log row, normalized from either an SMS or a voice-call Twilio record. */
export interface PhoneLogEntry {
  /** Twilio SID (Message or Call) — globally unique by prefix. */
  id: string;
  type: PhoneLogType;
  /** When the message was sent / the call started. */
  date: Date;
  /** Sender / caller phone number (E.164). */
  from: string;
  /** Recipient / callee phone number (E.164). */
  to: string;
  direction: string;
  status: string;
  /** Call duration in seconds (voice only). */
  duration?: string;
}

const smsToEntry = (entry: SMSLogEntry): PhoneLogEntry => ({
  id: entry.sid,
  type: "SMS",
  date: new Date(entry.dateSent),
  from: entry.from,
  to: entry.to,
  direction: entry.direction,
  status: entry.status,
});

const callToEntry = (entry: CallLogEntry): PhoneLogEntry => ({
  id: entry.sid,
  type: "Voice",
  date: new Date(entry.startTime),
  from: entry.from,
  to: entry.to,
  direction: entry.direction,
  status: entry.status,
  duration: entry.duration,
});

interface PhoneLogStream {
  entries: PhoneLogEntry[];
  /** Oldest timestamp (ms) on the last loaded page, or null if nothing loaded. */
  lastPageOldest: number | null;
  /** Whether the stream still has more pages to fetch. */
  hasMore: boolean;
}

/**
 * Merge two independently-paginated phone-log streams into one date-descending list using a
 * watermark so global ordering stays correct when one stream has loaded further back than the other.
 *
 * The watermark is the **newest** of each still-paginating stream's oldest-loaded entry: any row
 * older than that could be displaced by a not-yet-loaded row from the other stream, so we hold it
 * back until the lagging stream catches up. Exhausted streams (no more pages) don't constrain it.
 */
export const mergePhoneLogStreams = (sms: PhoneLogStream, calls: PhoneLogStream): PhoneLogEntry[] => {
  const constraints = [sms, calls].reduce<number[]>((acc, stream) => {
    if (stream.hasMore && stream.lastPageOldest !== null) acc.push(stream.lastPageOldest);
    return acc;
  }, []);
  const watermark = constraints.length > 0 ? Math.max(...constraints) : 0;

  return [...sms.entries, ...calls.entries]
    .filter((entry) => entry.date.getTime() >= watermark)
    .sort((a, b) => b.date.getTime() - a.date.getTime());
};

export interface UseTwilioPhoneLogsQueryArgs {
  /** Lower bound (server-side date filter on both streams). */
  from?: Date;
  /** Upper bound (server-side date filter on both streams). */
  to?: Date;
  /** Per-page size for each stream. Defaults to the server default (50). */
  pageSize?: number;
  enabled?: boolean;
}

export interface UseTwilioPhoneLogsQueryResult {
  logs: PhoneLogEntry[];
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isFetched: boolean;
  error: Error | null;
}

/**
 * Combined infinite query over Twilio SMS + voice-call logs.
 *
 * Fans out to {@link useTwilioSmsLogsQuery} and {@link useTwilioCallLogsQuery}, then merges both
 * cursor-paginated streams into a single date-descending list via {@link mergePhoneLogStreams}.
 * `fetchNextPage` advances whichever stream's oldest-loaded entry is newest, which fills the gap
 * just below the current watermark and keeps the merged ordering gap-free across page boundaries.
 *
 * Note: Twilio only supports date-range filtering + pagination server-side. Text search and
 * field-level filters must be applied client-side over the loaded rows.
 */
export const useTwilioPhoneLogsQuery = ({
  from,
  to,
  pageSize,
  enabled,
}: UseTwilioPhoneLogsQueryArgs = {}): UseTwilioPhoneLogsQueryResult => {
  const request = useMemo(
    () => ({ from: from?.toISOString(), to: to?.toISOString(), pageSize }),
    [from, to, pageSize]
  );

  const smsQuery = useTwilioSmsLogsQuery({ request, enabled });
  const callsQuery = useTwilioCallLogsQuery({ request, enabled });

  const smsEntries = useMemo(() => (smsQuery.data?.logs ?? []).map(smsToEntry), [smsQuery.data?.logs]);
  const callEntries = useMemo(() => (callsQuery.data?.logs ?? []).map(callToEntry), [callsQuery.data?.logs]);

  const logs = useMemo(
    () =>
      mergePhoneLogStreams(
        { entries: smsEntries, lastPageOldest: smsQuery.data?.lastPageOldest ?? null, hasMore: smsQuery.hasNextPage },
        {
          entries: callEntries,
          lastPageOldest: callsQuery.data?.lastPageOldest ?? null,
          hasMore: callsQuery.hasNextPage,
        }
      ),
    [smsEntries, callEntries, smsQuery.data?.lastPageOldest, smsQuery.hasNextPage, callsQuery.data?.lastPageOldest, callsQuery.hasNextPage]
  );

  const fetchNextPage = useCallback(async () => {
    const oldestOf = (entries: PhoneLogEntry[]): number | null =>
      entries.length > 0 ? entries.reduce((min, e) => Math.min(min, e.date.getTime()), Infinity) : null;

    const candidates = [
      { query: smsQuery, oldest: oldestOf(smsEntries) },
      { query: callsQuery, oldest: oldestOf(callEntries) },
    ].flatMap((c) => (c.query.hasNextPage && c.oldest !== null ? [{ query: c.query, oldest: c.oldest }] : []));

    // Fetch the stream whose oldest loaded entry is newest, filling the gap just below the watermark.
    const next = candidates.sort((a, b) => b.oldest - a.oldest)[0];
    if (next) await next.query.fetchNextPage();
  }, [smsQuery, callsQuery, smsEntries, callEntries]);

  return {
    logs,
    fetchNextPage,
    hasNextPage: smsQuery.hasNextPage || callsQuery.hasNextPage,
    isLoading: smsQuery.isLoading || callsQuery.isLoading,
    isFetchingNextPage: smsQuery.isFetchingNextPage || callsQuery.isFetchingNextPage,
    isFetched: smsQuery.isFetched && callsQuery.isFetched,
    error: smsQuery.error ?? callsQuery.error,
  };
};
